import dayjs from 'dayjs'
import { useRolodexEdit } from 'features/fde'
import { useRolodexEntries } from 'features/rolodex'
import { useCallback, useMemo } from 'react'
import { RolodexEntryTagsType, RolodexEntryType } from 'types'
import { v4 } from 'uuid'
import z from 'zod'

import { ROLODEX_DOCUMENT_ONTOLOGY_TYPE } from './useDocumentOntologyAdditions'

export const ROLODEX_DOC_FIELDS_TYPE = 'document-fields'

const DOC_FIELDS_DATA_SCHEMA = z.object({
  title: z.string().default('Untitled'),
  fields: z.record(z.string(), z.unknown()),
})

export type DocumentSectionFields = {
  id: string
  title: string
  fields: Record<string, unknown>
  documentOntologyId: string | undefined
  tags: RolodexEntryTagsType
  createdAt: string
}

export function blankDocumentSectionFields(): DocumentSectionFields {
  return {
    id: `fields-${v4().substring(0, 6)}`,
    title: 'Untitled',
    fields: {},
    documentOntologyId: undefined,
    tags: {},
    createdAt: dayjs().add(1, 'year').format('YYYY-MM-DDTHH:mm:ss.SSSZZ'),
  }
}

export function useDocumentSectionFields(tags: RolodexEntryTagsType): {
  documentSectionFields: ReadonlyMap<string, DocumentSectionFields>
  isLoading: boolean
  forceRefresh: () => Promise<void>
} {
  const { entries, isLoading, forceRefresh } = useRolodexEntries(
    useMemo(
      () => ({
        type: ROLODEX_DOC_FIELDS_TYPE as RolodexEntryType,
        tags,
      }),
      [tags]
    )
  )

  return useMemo(() => {
    return {
      documentSectionFields: new Map(
        entries.map((entry) => [
          entry.id,
          {
            id: entry.id,
            ...DOC_FIELDS_DATA_SCHEMA.parse(entry.data),
            documentOntologyId:
              entry.tags[ROLODEX_DOCUMENT_ONTOLOGY_TYPE] ?? undefined,
            tags: entry.tags,
            createdAt: entry.created_at ?? '',
          },
        ])
      ),
      isLoading,
      forceRefresh,
    }
  }, [entries, isLoading, forceRefresh])
}

export const useSaveDocumentSectionFields = (): {
  doSave: (
    documentSectionFields:
      | DocumentSectionFields
      | ReadonlyArray<DocumentSectionFields>
  ) => Promise<void>
} => {
  const { rolodexPatch } = useRolodexEdit()
  const doSave = useCallback(
    async (
      documentSectionFields:
        | DocumentSectionFields
        | ReadonlyArray<DocumentSectionFields>
    ) => {
      if (!documentSectionFields) {
        return
      }
      const normalizedDocSectionFields = Array.isArray(documentSectionFields)
        ? documentSectionFields
        : [documentSectionFields]
      if (!normalizedDocSectionFields.length) {
        return
      }
      await rolodexPatch(
        normalizedDocSectionFields.map((sectionFields) => ({
          id: sectionFields.id,
          type: ROLODEX_DOC_FIELDS_TYPE as RolodexEntryType,
          data: {
            title: sectionFields.title,
            fields: sectionFields.fields,
          },
          tags: {
            ...sectionFields.tags,
            [ROLODEX_DOCUMENT_ONTOLOGY_TYPE]: sectionFields.documentOntologyId,
          },
        }))
      )
    },
    [rolodexPatch]
  )
  return useMemo(() => ({ doSave }), [doSave])
}

export function useSaveDocumentNotes(): {
  doSave: (id: string, notes: string | undefined) => Promise<void>
} {
  const { rolodexPatch } = useRolodexEdit()
  const doSave = useCallback(
    async (id: string, notes: string | undefined) => {
      await rolodexPatch([
        { id, ...(notes ? { data: { notes } } : { delete_data: ['notes'] }) },
      ])
    },
    [rolodexPatch]
  )
  return useMemo(() => ({ doSave }), [doSave])
}
